import * as yup from 'yup';

export const ContractorInputs = {
    // PERSONAL INFO
    FIRSTNAME: 'firstname',
    MIDDLENAME: 'middlename',
    LASTNAME: 'lastname',
    CONTACT_EMAIL: 'contact_email',
    CONTRACT_START_DATE: 'start_date',
    PAYMENT_METHOD: 'payment_method',
    CONTRACTOR_TYPE: 'contractor_type',
    TERMINATION_DATE: 'termination_date',
    DEPARTMENT_ID: 'department_id',
    PRIMARY_WORKPLACE_ID: 'primary_workplace_id',

    // CONTACT INFO
    SSN: 'ssn',
    EIN: 'ein',
    DATE_OF_BIRTH: 'date_of_birth',
    STREET_1: 'street1',
    STREET_2: 'street2',
    CITY: 'city',
    STATE: 'state',
    ZIP_CODE: 'zip_code',

    HOME_PHONE: 'home_phone',
    CELL_PHONE: 'cell_phone',

    // WORK INFO (UI only — not sent to API)
    IS_WORK_FROM_HOME: 'is_work_from_home',
    WORK_LOCATION: 'work_location',
    WORK_TIME_ZONE: 'work_timezone',

    // WAGE INFORMATION
    WAGE: 'wage',
    WAGE_PERIOD: 'wage_period',

    // BUSINESS INFO (shown for Business contractor type)
    BUSINESS_NAME: 'business_name',
    TRADE_NAME: 'trade_name',
    SERVICE_TYPE: 'service_type',
    BUSINESS_ADDRESS: 'business_address',

    // EMERGENCY CONTACT
    EMERGENCY_FIRST_NAME: 'emergency_first_name',
    EMERGENCY_LAST_NAME: 'emergency_last_name',
    EMERGENCY_RELATIONSHIP: 'emergency_relationship',
    EMERGENCY_PHONE: 'emergency_phone',
    EMERGENCY_ALT_PHONE: 'emergency_alt_phone',
    EMERGENCY_EMAIL: 'emergency_email',
}

export const contractorSchema = yup.object({
    [ContractorInputs.FIRSTNAME]: yup.string().required('Please enter first name'),
    [ContractorInputs.MIDDLENAME]: yup.string().nullable().default(''),
    [ContractorInputs.LASTNAME]: yup.string().required('Please enter last name'),
    [ContractorInputs.CONTACT_EMAIL]: yup.string().email('Please enter valid email').required('Email is required'),
    [ContractorInputs.CONTRACT_START_DATE]: yup.string().required('Start date is required'),
    [ContractorInputs.PAYMENT_METHOD]: yup.string().required('Payment method is required'),
    [ContractorInputs.CONTRACTOR_TYPE]: yup.string().required('Contractor type is required'),
    [ContractorInputs.TERMINATION_DATE]: yup.string().nullable().default(''),
    [ContractorInputs.DEPARTMENT_ID]: yup.string().nullable().default(''),
    [ContractorInputs.PRIMARY_WORKPLACE_ID]: yup.string().nullable().default(''),
    [ContractorInputs.SSN]: yup.string().nullable().when(ContractorInputs.CONTRACTOR_TYPE, {
        is: (val) => !val || val.toLowerCase() === 'individual',
        then: (schema) => schema.required('SSN is required for individual contractors'),
        otherwise: (schema) => schema.nullable().default(''),
    }),
    [ContractorInputs.EIN]: yup.string().nullable().when(ContractorInputs.CONTRACTOR_TYPE, {
        is: (val) => val && val.toLowerCase() === 'business',
        then: (schema) => schema.required('EIN is required for business contractors').matches(/^\d{2}-\d{7}$/, 'EIN must be in format XX-XXXXXXX'),
        otherwise: (schema) => schema.nullable().default(''),
    }),
    [ContractorInputs.DATE_OF_BIRTH]: yup.string()
        .required('Date of birth is required')
        .test('dob-valid', 'Please enter a valid date of birth', v => !!v && !isNaN(Date.parse(v)))
        .test('dob-past', 'Date of birth must be in the past', v => !!v && new Date(v) < new Date())
        .test('dob-age', 'Contractor must be at least 18 years old', v => {
            if (!v) return false;
            const dob = new Date(v);
            const cutoff = new Date();
            cutoff.setFullYear(cutoff.getFullYear() - 18);
            return dob <= cutoff;
        }),
    [ContractorInputs.HOME_PHONE]: yup.string().nullable().default('')
        .test('home-phone-digits', 'Home phone must be 10 digits', v => !v || v.replace(/\D/g, '').length === 10),
    [ContractorInputs.CELL_PHONE]: yup.string()
        .required('Cell phone is required')
        .test('cell-phone-digits', 'Cell phone must be 10 digits', v => !!v && v.replace(/\D/g, '').length === 10),

    // UI-only fields — not sent to API
    [ContractorInputs.STREET_1]: yup.string().required('Street address is required'),
    [ContractorInputs.STREET_2]: yup.string().nullable().default(''),
    [ContractorInputs.CITY]: yup.string().required('City is required'),
    [ContractorInputs.STATE]: yup.string().required('State is required'),
    [ContractorInputs.ZIP_CODE]: yup.string()
        .required('Zip code is required')
        .matches(/^\d{5}(-\d{4})?$/, 'Enter a valid 5-digit zip code'),
    [ContractorInputs.IS_WORK_FROM_HOME]: yup.string().nullable().default(''),
    [ContractorInputs.WORK_LOCATION]: yup.string().nullable().default(''),
    [ContractorInputs.WORK_TIME_ZONE]: yup.string().nullable().default(''),
    [ContractorInputs.WAGE]: yup.number()
        .typeError('Wage must be a number')
        .required('Wage / pay rate is required')
        .min(0.01, 'Wage must be greater than 0'),
    [ContractorInputs.WAGE_PERIOD]: yup.string().required('Pay period / type is required'),

    // Business info — optional
    [ContractorInputs.BUSINESS_NAME]: yup.string().nullable().default(''),
    [ContractorInputs.TRADE_NAME]: yup.string().nullable().default(''),
    [ContractorInputs.SERVICE_TYPE]: yup.string().nullable().default(''),
    [ContractorInputs.BUSINESS_ADDRESS]: yup.string().nullable().default(''),

    // Emergency contact — optional
    [ContractorInputs.EMERGENCY_FIRST_NAME]: yup.string().nullable().default(''),
    [ContractorInputs.EMERGENCY_LAST_NAME]: yup.string().nullable().default(''),
    [ContractorInputs.EMERGENCY_RELATIONSHIP]: yup.string().nullable().default(''),
    [ContractorInputs.EMERGENCY_PHONE]: yup.string().nullable().default('')
        .test('em-phone-digits', 'Primary phone must be 10 digits', v => !v || v.replace(/\D/g, '').length === 10),
    [ContractorInputs.EMERGENCY_ALT_PHONE]: yup.string().nullable().default('')
        .test('em-alt-phone-digits', 'Alternate phone must be 10 digits', v => !v || v.replace(/\D/g, '').length === 10),
    [ContractorInputs.EMERGENCY_EMAIL]: yup.string().email('Enter a valid email').nullable().default(''),
})


